/**
 * Error Boundary Component
 *
 * Catches render and effect errors below it, reports them to analytics, and
 * shows a recovery UI. Two behaviours worth knowing:
 *
 *   - A stale-chunk error (lazy import 404 after a deploy) reloads the page
 *     once instead of showing the error screen; the wizard's sessionStorage
 *     snapshot brings the visitor's inputs back.
 *   - "Reset Estimator" only helps if `onReset` actually resets something. The
 *     wizard passes its context reset; without it the same state re-renders
 *     and a deterministic crash simply recurs.
 */

import { tr } from '../../i18n/standalone';
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  ErrorScope,
  isChunkLoadError,
  isWebGLUnavailableError,
  reloadForStaleChunk,
  reportCapabilityGap,
  reportClientError,
} from '../../utils/errorReporting';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  /** Tag for the analytics report so a 3D-viewer failure is distinguishable from a wizard crash. */
  scope?: ErrorScope;
  /** Replace the default full recovery screen (e.g. a small placeholder for a widget). */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  reloading: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const firstFrame = (info.componentStack || '').trim().split('\n')[0] || '';
    const scope = this.props.scope ?? 'wizard';
    // A 3D viewer that can't get a WebGL context is a device limit the
    // placeholder already handles — count it, don't file it as a bug.
    if (scope === '3d-viewer' && isWebGLUnavailableError(error)) {
      reportCapabilityGap('webgl', error);
    } else {
      reportClientError({
        scope,
        error,
        extra: { component_stack: firstFrame.trim() },
      });
    }

    if (isChunkLoadError(error) && reloadForStaleChunk()) {
      this.setState({ reloading: true });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.reloading) return null;

    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback;

      return (
        <div className="error-boundary" style={{
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '500px',
          margin: '2rem auto',
        }}>
          <AlertTriangle size={48} style={{ color: '#e53e3e', marginBottom: '1rem' }} />
          <h2 style={{ marginBottom: '0.5rem' }}>{tr('common:error.title')}</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            {tr('common:error.body')}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.handleReset}
          >
            {tr('common:error.reset')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
